import { Component, Input, Output, EventEmitter, OnInit, AfterViewInit, OnChanges, OnDestroy, SimpleChanges, ChangeDetectionStrategy, ChangeDetectorRef, Injector, ViewChild, ElementRef } from '@angular/core';
import { DateTime } from 'luxon';
import { DebugService } from '../../services/debug.service';
import { DestroyGuard } from 'services/browser-utils';
import { hasClientSmallScreen } from 'environments/environment-helpers';
import { TaskStatus } from '../cs-task-calendar-day/cs-task-calendar-day';
import { formatWeekdayNarrow } from '@cf-shared/date-format';
import { BOOT_LOCALE } from 'i18n/i18n.service';

// Re-export TaskStatus for backward compatibility
export { TaskStatus } from '../cs-task-calendar-day/cs-task-calendar-day';

export interface CalendarDate {
  date: DateTime;
  /**
   * The day as a plain ISO date string, e.g. "2026-07-29".
   *
   * Kept as a field rather than formatted in the template: the strip holds about
   * a month of days, and this component uses OnPush, so a template call would
   * re-format every one of them on each change-detection pass. It is also what
   * the template puts in `data-date`, which is how an E2E test addresses one
   * particular day — the same thing the full-page calendar does
   * (`task-calendar-page.html`).
   */
  dateKey: string;
  dayOfMonth: number;
  dayOfWeek: string;
  taskStatus: TaskStatus;
  isSelected?: boolean;
  isToday?: boolean;
}

@Component({
  selector: 'cs-task-calendar',
  templateUrl: 'cs-task-calendar.html',
  host: {
    'class': 'task-calendar',
    '[style.--calendar-gap.px]': 'calendarGap'
  },
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CsTaskCalendar implements OnInit, AfterViewInit, OnChanges, OnDestroy {
  private destroyGuard = new DestroyGuard();
  @ViewChild('container', { read: ElementRef, static: true }) containerRef: ElementRef<HTMLElement>;

  @Input() taskStatuses: Map<string, TaskStatus> = new Map();
  @Input() selectedDate?: DateTime;
  @Output() selectedDateChange = new EventEmitter<DateTime>(); // For two-way binding

  private selectedDateTime?: DateTime;

  dates: CalendarDate[] = [];
  calendarGap: number = 12; // default gap
  private resizeObserver?: ResizeObserver;
  private visibilityObserver?: IntersectionObserver;
  private hasScrolledToToday = false;
  showLeftEdgeButton = false;
  showRightEdgeButton = false;
  private selectedDateObserver?: IntersectionObserver;

  constructor(
    private cd: ChangeDetectorRef,
    private debug: DebugService,
    private injector: Injector
  ) {}

  ngOnInit() {
    void this.generateCalendarDates();
    void this.setupResizeObserver();
  }

  ngAfterViewInit() {
    // set up observer to detect when the calendar becomes visible
    this.setupVisibilityObserver();
    // check initial button visibility
    void this.destroyGuard.safeWaitForAngularUpdate(this.injector).then((alive) => {
      if (!alive) return;
      this.updateEdgeButtonVisibility();
    });
  }

  ngOnDestroy() {
    this.destroyGuard.destroy();
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    if (this.visibilityObserver) {
      this.visibilityObserver.disconnect();
    }
    if (this.selectedDateObserver) {
      this.selectedDateObserver.disconnect();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['selectedDate']) {
      const previousDateTime = changes['selectedDate'].previousValue ?
        changes['selectedDate'].previousValue.startOf('day') : undefined;
      const newDateTime = this.selectedDate ?
        this.selectedDate.startOf('day') : undefined;

      this.selectedDateTime = newDateTime;

      if (!changes['selectedDate'].firstChange) {
        this.updateSelectedDate();

        // Only scroll if the date actually changed (not just the object reference)
        const dateActuallyChanged = !previousDateTime || !newDateTime ||
          !previousDateTime.hasSame(newDateTime, 'day');

        if (dateActuallyChanged) {
          // Scroll to the newly selected date
          void this.scrollToSelected().then(() => {
            if (this.destroyGuard.isDestroyed) return;
            // update button visibility after scrolling
            this.updateEdgeButtonVisibility();
          });
        }
      }
    }
    if (changes['taskStatuses']) {
      void this.applyTaskStatusChangesAsync();
    }
  }

  /**
   * Refresh the day markers WITHOUT disturbing where the user has scrolled to.
   *
   * This matters most during a long catch-up sync, which is exactly when the
   * markers change most often: statuses arrive in waves, and rebuilding the
   * strip on each wave used to end with a scroll back to the selected day. The
   * user could not scroll through their history at all — every wave yanked the
   * strip back to today, several times a minute.
   *
   * So when the set of DAYS is unchanged and only their statuses moved, update
   * the existing cells in place. A full rebuild (and the scroll that goes with
   * it) is kept for the case that actually needs it: the strip now covers a
   * different span of days.
   */
  private async applyTaskStatusChangesAsync() {
    const range = this.resolveDateRange();
    const coversSameDays = this.dates.length > 0
      && this.dates[0].date.hasSame(range.startDate, 'day')
      && this.dates[this.dates.length - 1].date.hasSame(range.endDate, 'day');

    if (!coversSameDays) {
      await this.generateCalendarDates();
      return;
    }

    let anyStatusChanged = false;
    for (const day of this.dates) {
      const freshStatus = this.taskStatuses.get(day.date.toISODate()) || TaskStatus.NoTask;
      if (day.taskStatus !== freshStatus) {
        day.taskStatus = freshStatus;
        anyStatusChanged = true;
      }
    }
    // only wake change detection when something actually moved — during a sync
    // most waves leave the visible days untouched. The destroy guard is required
    // even though nothing is awaited above today: this method is async, so an
    // await added later would let markForCheck fire on a destroyed view (NG0911).
    if (anyStatusChanged && !this.destroyGuard.isDestroyed) {
      this.cd.markForCheck();
    }
  }

  /** The span of days the strip should cover, from the statuses it was given. */
  private resolveDateRange(): { startDate: DateTime; endDate: DateTime } {
    // if task statuses are provided, derive the date range from them
    if (this.taskStatuses && this.taskStatuses.size > 0) {
      const dateKeys = Array.from(this.taskStatuses.keys()).sort();
      return {
        startDate: DateTime.fromISO(dateKeys[0]),
        endDate: DateTime.fromISO(dateKeys[dateKeys.length - 1]),
      };
    }
    // fallback to default range if no statuses provided: most right element is
    // today + 2, most left is one month before that
    const endDate = this.debug.getNowLocal().startOf('day').plus({ days: 2 });
    return { startDate: endDate.minus({ months: 1 }), endDate };
  }

  private updateSelectedDate() {
    // update the selected state for all dates
    this.dates.forEach(d => {
      d.isSelected = this.selectedDateTime ? d.date.hasSame(this.selectedDateTime, 'day') : false;
    });
    this.cd.markForCheck();
  }

  async generateCalendarDates() {
    const today = this.debug.getNowLocal().startOf('day');
    const { startDate, endDate } = this.resolveDateRange();

    this.dates = [];
    let currentDate = startDate;

    while (currentDate <= endDate) {
      const dateKey = currentDate.toISODate(); // use Luxon's ISO date format
      const taskStatus = this.taskStatuses.get(dateKey) || TaskStatus.NoTask;

      this.dates.push({
        date: currentDate,
        dateKey: dateKey,
        dayOfMonth: currentDate.day,
        // single weekday letter in the active UI language (#4317)
        dayOfWeek: formatWeekdayNarrow(currentDate, BOOT_LOCALE),
        taskStatus: taskStatus,
        isToday: currentDate.hasSame(today, 'day'),
        isSelected: this.selectedDateTime ? currentDate.hasSame(this.selectedDateTime, 'day') : false
      });

      currentDate = currentDate.plus({ days: 1 });
    }

    // update button visibility after generating dates
    if (!await this.destroyGuard.safeWaitForAngularUpdate(this.injector)) return;

    this.calculateGap(this.containerRef?.nativeElement.offsetWidth);

    this.updateEdgeButtonVisibility();

    // scroll to selected element after regenerating calendar dates
    // this ensures that if the dates were regenerated while a scroll was in progress,
    // we scroll to the correct position with the new elements.
    // 'instant' on purpose: the task statuses arrive async (often right after the
    // page renders), so this path runs on every page entry. An animated scroll here
    // shows as a visible "auto scroll to today" glitch. Animated scrolling is
    // reserved for the edge skip buttons (scrollToSelectedFromButton).
    if (this.selectedDateTime && this.dates.some(d => d.isSelected)) {
      await this.scrollToSelected(false, 'instant');
    }
  }

  async scrollToSelected(scrollToToday: boolean = false, behavior: 'smooth' | 'instant' = 'smooth') {
    // wait for container and its content to be ready
    if (!await this.destroyGuard.safeWaitForAngularUpdate(this.injector)) return;

    // find the target element index - either today or selected
    let targetIndex: number;
    if (scrollToToday) {
      targetIndex = this.dates.findIndex(d => d.isToday);
    } else {
      targetIndex = this.dates.findIndex(d => d.isSelected);
    }

    if (targetIndex === -1) return; // target not in view

    if (!this.containerRef) return;
    const container = this.containerRef.nativeElement;

    const dateElements = container.querySelectorAll('cs-task-calendar-day');
    if (!dateElements[targetIndex]) return;

    const targetElement = dateElements[targetIndex] as HTMLElement;

    // check if the target date is already visible in the container
    const containerRect = container.getBoundingClientRect();
    const elementRect = targetElement.getBoundingClientRect();

    // check if element is fully visible
    const isVisible = elementRect.left >= containerRect.left &&
                     elementRect.right <= containerRect.right;

    // only scroll if not visible
    if (!isVisible) {
      // try to scroll to target + 2, or the last available element after target
      const scrollIndex = Math.min(targetIndex + 2, dateElements.length - 1);
      const element = dateElements[scrollIndex] || dateElements[targetIndex];

      if (element) {
        // scroll to show target with 2 dates after it (or as many as available)
        // using 'end' keeps the target at the right edge, showing target date with context
        (element as HTMLElement).scrollIntoView({
          behavior: behavior,
          block: 'nearest',
          inline: 'end'
        });
      }
    }
  }

  /**
   * A tap only ASKS for a day. It does not mark one selected — the
   * `selectedDate` input decides that, so the strip can never show a day the
   * page dropped. Do not highlight the tapped cell here; the commit that
   * removed that says what went wrong when it did.
   *
   * The emit stays SYNCHRONOUS: `design-docs/reactive-sync.md` records that the
   * rapid-pair E2E test relies on a pick reaching `DayAnimationQueue.tryStart`
   * within the click's own task, so nothing in this chain may be deferred.
   */
  onDateClick(calendarDate: CalendarDate) {
    this.selectedDateChange.emit(calendarDate.date);
  }


  onWheel(event: WheelEvent) {
    // prevent vertical scrolling and convert to horizontal
    event.preventDefault();
    const container = event.currentTarget as HTMLElement;

    // scroll horizontally based on wheel delta
    // deltaY is for vertical wheel movement, we convert it to horizontal
    container.scrollLeft += event.deltaY || event.deltaX;
  }

  private updateEdgeButtonVisibility() {
    // this method now sets up the intersection observer for today
    const todayIndex = this.dates.findIndex(d => d.isToday);

    // disconnect previous observer if it exists
    if (this.selectedDateObserver) {
      this.selectedDateObserver.disconnect();
      this.selectedDateObserver = null;
    }

    if (todayIndex === -1) {
      // no today date in view, hide both buttons
      this.showLeftEdgeButton = false;
      this.showRightEdgeButton = false;
      this.cd.markForCheck();
      return;
    }

    if (!this.containerRef) return;
    const container = this.containerRef.nativeElement;

    const dateElements = container.querySelectorAll('cs-task-calendar-day');
    const todayElement = dateElements[todayIndex] as HTMLElement;
    if (!todayElement) return;

    // create intersection observer for today's element
    this.selectedDateObserver = new IntersectionObserver(
      (entries) => {
        if (this.destroyGuard.isDestroyed) return;
        entries.forEach(entry => {
          const isVisible = entry.isIntersecting;

          if (!isVisible) {
            // element is not visible, determine which side it's on
            const containerRect = container.getBoundingClientRect();
            const elementRect = entry.target.getBoundingClientRect();

            // check if today is to the left of visible area
            this.showLeftEdgeButton = elementRect.right < containerRect.left;

            // check if today is to the right of visible area
            this.showRightEdgeButton = elementRect.left > containerRect.right;
          } else {
            // element is visible, hide both buttons
            this.showLeftEdgeButton = false;
            this.showRightEdgeButton = false;
          }

          this.cd.markForCheck();
        });
      },
      {
        root: container,
        threshold: [0, 1.0], // trigger when element enters/exits view
        rootMargin: '0px'
      }
    );

    // observe today's element
    this.selectedDateObserver.observe(todayElement);
  }

  scrollToSelectedFromButton() {
    // scroll to today with smooth behavior for button clicks
    void this.scrollToSelected(true, 'smooth');
    // buttons will automatically hide via intersection observer
  }

  private async setupResizeObserver() {
    // wait for container to be available
    if (!await this.destroyGuard.safeWaitForAngularUpdate(this.injector)) return;
    if (!this.containerRef) {
      if (!await this.destroyGuard.safeWaitForAngularUpdate(this.injector)) return;
      void this.setupResizeObserver();
      return;
    }

    const container = this.containerRef.nativeElement;

    this.resizeObserver = new ResizeObserver((entries: ResizeObserverEntry[]) => {
      for (const entry of entries) {
        this.calculateGap(entry.contentRect.width);
        // scroll to keep selected item visible on resize.
        // 'instant' on purpose: this also fires during page-entry layout, and an
        // animated scroll there shows as a visible "auto scroll to today" glitch.
        // Animated scrolling is reserved for the edge skip buttons.
        void this.scrollToSelected(false, 'instant');
      }
    });

    this.resizeObserver.observe(container);
  }

  private calculateGap(containerWidth: number) {
    const isSmallScreen = hasClientSmallScreen();
    const itemWidth = isSmallScreen ? 34 : 36; // actual width from CSS
    const targetItems = isSmallScreen ? 7 : 14;

    // Calculate gap so first and last items touch container edges
    // Total space for gaps = container width - (all items width)
    // Number of gaps = items - 1 (gaps only between items)
    const totalItemsWidth = targetItems * itemWidth;
    const availableGapSpace = containerWidth - totalItemsWidth;
    const gapCount = targetItems - 1;

    // Use floor to avoid subpixel issues
    this.calendarGap = availableGapSpace > 0 ? Math.round(availableGapSpace / gapCount) : 0;
    this.cd.markForCheck();
  }

  private setupVisibilityObserver() {
    if (!this.containerRef) {
      // try again after next update
      void this.destroyGuard.safeWaitForAngularUpdate(this.injector).then((alive) => {
        if (!alive) return;
        this.setupVisibilityObserver();
      });
      return;
    }
    const container = this.containerRef.nativeElement;

    // create observer to detect when calendar is visible
    this.visibilityObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach(async entry => {
          if (this.destroyGuard.isDestroyed) return;
          // when calendar becomes visible and we haven't scrolled yet
          if (entry.isIntersecting && !this.hasScrolledToToday && this.dates.length > 0 && container.offsetWidth > 0) {
            // ensure gap is calculated before scrolling
            this.calculateGap(container.offsetWidth);

            // wait for Angular to update the view with new gap
            if (!await this.destroyGuard.safeWaitForAngularUpdate(this.injector)) return;

            // scroll to selected with instant behavior (no animation on initial load)
            void this.scrollToSelected(false, 'instant');
            this.hasScrolledToToday = true;

            // disconnect observer after first scroll
            if (this.visibilityObserver) {
              this.visibilityObserver.disconnect();
              this.visibilityObserver = null;
            }
          }
        });
      },
      {
        threshold: 0.1 // trigger when at least 10% visible
      }
    );

    this.visibilityObserver.observe(container);
  }
}